import React from 'react';
import { Box, Container, Skeleton, Paper } from '@mui/material';

function ObservationsSkeleton() {
  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Page Header */}
      <Box 
        sx={{ 
          mb: 4,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Box>
          <Skeleton variant="text" width={280} height={48} sx={{ mb: 1 }} />
          <Skeleton variant="text" width={420} height={28} />
        </Box>
        
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
          <Skeleton variant="rounded" width={180} height={42} />
          <Skeleton variant="rounded" width={120} height={42} />
        </Box>
      </Box>

      {/* Pending Proposals Section */}
      <Paper 
        elevation={0} 
        sx={{ 
          p: 3, 
          mb: 3,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Skeleton variant="circular" width={32} height={32} />
          <Skeleton variant="text" width={240} height={32} />
        </Box>
        
        {/* Proposal Cards */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {[1, 2].map((i) => (
            <Paper 
              key={i}
              elevation={0}
              sx={{ 
                p: 2.5,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1.5,
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <Skeleton variant="text" width="60%" height={28} sx={{ mb: 1 }} />
                  <Skeleton variant="text" width="40%" height={24} />
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Skeleton variant="rounded" width={90} height={36} />
                  <Skeleton variant="rounded" width={90} height={36} />
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                <Skeleton variant="text" width={120} height={24} />
                <Skeleton variant="text" width={140} height={24} />
                <Skeleton variant="text" width={100} height={24} />
              </Box>
            </Paper>
          ))}
        </Box>
      </Paper>

      {/* Chart Controls */}
      <Paper 
        elevation={0} 
        sx={{ 
          p: 3, 
          mb: 3,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Indicator Selection */}
          <Box>
            <Skeleton variant="text" width={180} height={28} sx={{ mb: 1.5 }} />
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                <Skeleton 
                  key={i}
                  variant="rounded" 
                  width={100} 
                  height={32}
                  sx={{ borderRadius: 4 }}
                />
              ))}
            </Box>
          </Box>

          {/* Time Range Selection */}
          <Box>
            <Skeleton variant="text" width={140} height={28} sx={{ mb: 1.5 }} />
            <Box sx={{ display: 'flex', gap: 1 }}>
              {['3M', '6M', '1Y', '2Y', '5Y', 'All'].map((label) => (
                <Skeleton 
                  key={label}
                  variant="rounded" 
                  width={60} 
                  height={36}
                  sx={{ borderRadius: 1 }}
                />
              ))}
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Chart Cards Grid */}
      <Box sx={{ 
        display: 'flex', 
        flexWrap: 'wrap', 
        gap: 2,
        '& > *': { 
          flex: '1 1 400px',
          minWidth: 0,
        }
      }}>
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <Paper
            key={i}
            elevation={0}
            sx={{
              p: 3,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              '&:hover': {
                borderColor: 'primary.main',
                boxShadow: 1,
              },
            }}
          >
            {/* Chart Header */}
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                <Skeleton variant="text" width="60%" height={32} />
                <Skeleton variant="rounded" width={80} height={28} sx={{ borderRadius: 4 }} />
              </Box>
              <Skeleton variant="text" width="40%" height={24} />
            </Box>

            {/* Latest Value Display */}
            <Box sx={{ mb: 3, display: 'flex', alignItems: 'baseline', gap: 1 }}>
              <Skeleton variant="text" width={120} height={48} />
              <Skeleton variant="text" width={80} height={32} />
            </Box>

            {/* Chart Area */}
            <Box sx={{ position: 'relative', height: 200 }}>
              <Skeleton 
                variant="rectangular" 
                width="100%" 
                height="100%" 
                sx={{ borderRadius: 1 }}
              />
              {/* Animated line effect */}
              <Box
                sx={{
                  position: 'absolute',
                  bottom: '20%',
                  left: 0,
                  right: 0,
                  height: 2,
                  background: 'linear-gradient(90deg, transparent, rgba(99, 102, 241, 0.3), transparent)',
                  animation: 'shimmer 2s infinite',
                  '@keyframes shimmer': {
                    '0%': { transform: 'translateX(-100%)' },
                    '100%': { transform: 'translateX(100%)' },
                  },
                }}
              />
            </Box>

            {/* Chart Footer */}
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Skeleton variant="text" width={140} height={24} />
              <Skeleton variant="text" width={100} height={24} />
            </Box>
          </Paper>
        ))}
      </Box>
    </Container>
  );
}

export default ObservationsSkeleton;

